import { buildTrafficContextTags } from "./sentryEventFiltersHelpers";

/** CefSharp / email-scanner bridge failures (e.g. Microsoft Safe Links). */
const CEFSHARP_BRIDGE_PATTERN = /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/i;

const AUTOMATED_SCANNER_MESSAGE_PATTERNS: RegExp[] = [
  CEFSHARP_BRIDGE_PATTERN,
  /MethodName:update,\s*ParamCount:\d+/i,
];

const AUTOMATED_SCANNER_USER_AGENT_PATTERNS: RegExp[] = [
  /CefSharp/i,
  /HeadlessChrome/i,
  /SafeLinks/i,
  /LinkPreview/i,
];

/**
 * Get Rejection Message.
 */
export function getRejectionMessage(reason: unknown): string {
  if (reason instanceof Error) {
    return reason.message;
  }
  if (typeof reason === "string") {
    return reason;
  }
  if (reason && typeof reason === "object" && "message" in reason) {
    const { message } = reason as { message?: unknown };
    if (typeof message === "string") {
      return message;
    }
  }
  return "";
}

export function isAutomatedScannerMessage(message: string): boolean {
  if (!message) {
    return false;
  }
  return AUTOMATED_SCANNER_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
}

export function isAutomatedScannerUserAgent(userAgent: string): boolean {
  if (!userAgent) {
    return false;
  }
  return AUTOMATED_SCANNER_USER_AGENT_PATTERNS.some((pattern) => pattern.test(userAgent));
}

const SCALE_QA_EMAIL_PATTERN = /^scale\.[^@]+@qa\.tellerwand\.com$/i;

function resolveDropCheckUserAgent(event: {
  request?: { headers?: Record<string, string> };
  contexts?: Record<string, unknown>;
}): string {
  const headers = event.request?.headers;
  const headerUserAgent =
    headers && typeof headers["User-Agent"] === "string" ? headers["User-Agent"] : "";
  const browserContext = event.contexts?.browser as
    | { user_agent?: string; name?: string }
    | undefined;
  const browserUserAgent =
    typeof browserContext?.user_agent === "string" ? browserContext.user_agent : "";
  const browserName = typeof browserContext?.name === "string" ? browserContext.name : "";
  return `${headerUserAgent} ${browserUserAgent} ${browserName}`.trim();
}

/**
 * Scale-test / HeadlessChrome / Safe Links traffic should not open Sentry issues.
 */
export function shouldDropAutomatedScannerEvent(event: {
  tags?: Record<string, string | number | boolean | null | undefined>;
  user?: { email?: string | null };
  request?: { headers?: Record<string, string> };
  contexts?: Record<string, unknown>;
}): boolean {
  const { tags } = event;
  if (tags?.traffic_class === "automated_scanner" || tags?.scanner_user_agent === "true") {
    return true;
  }

  const email = event.user?.email;
  if (typeof email === "string" && SCALE_QA_EMAIL_PATTERN.test(email)) {
    return true;
  }

  return isAutomatedScannerUserAgent(resolveDropCheckUserAgent(event));
}

export function getSentryEventMessage(
  event: { message?: unknown; exception?: { values?: Array<{ value?: string }> } },
  hint?: { originalException?: unknown }
): string {
  const eventMessage = typeof event.message === "string" ? event.message : "";
  const exceptionValue = event.exception?.values?.[0]?.value ?? "";
  const originalMessage = hint?.originalException
    ? getRejectionMessage(hint.originalException)
    : "";
  return `${eventMessage} ${exceptionValue} ${originalMessage}`.trim();
}

export function enrichSentryEventWithTrafficContext<
  T extends {
    tags?: Record<string, string | number | boolean | null | undefined>;
    user?: { id?: string | number };
    contexts?: Record<string, unknown>;
    request?: { headers?: Record<string, string> };
  },
>(event: T, hint?: { originalException?: unknown }): T {
  event.tags = buildTrafficContextTags(event, hint);
  return event;
}

function getExceptionMessage(originalException: unknown): string {
  if (!originalException || typeof originalException !== "object") {
    return "";
  }
  const { message } = originalException as { message?: unknown };
  return typeof message === "string" ? message : "";
}

function parseExceptionStatus(rawStatus: unknown): number {
  if (typeof rawStatus === "number") {
    return rawStatus;
  }
  if (typeof rawStatus === "string") {
    return Number(rawStatus);
  }
  return Number.NaN;
}

export function shouldDropLocalDevEvent(requestUrl: string, message: string): boolean {
  if (requestUrl.includes("127.0.0.1") || requestUrl.includes("localhost")) {
    return true;
  }
  return (
    message.includes("window.__ENV__") ||
    message.includes("config.js") ||
    message.includes("Config.js not loaded")
  );
}

export function shouldDropRouteNotFoundEvent(message: string): boolean {
  return (
    message.includes("404 Error: User attempted to access non-existent route") ||
    message.includes("404: User attempted to access non-existent route")
  );
}

/**
 * Should Drop Split Mage Or Auth Event.
 */
export function shouldDropSplitMageOrAuthEvent(originalException: unknown): boolean {
  if (!originalException || typeof originalException !== "object") {
    return false;
  }

  const ex = originalException as { name?: unknown; status?: unknown; endpoint?: unknown };
  const name = typeof ex.name === "string" ? ex.name : "";
  const status = parseExceptionStatus(ex.status);
  const endpoint = typeof ex.endpoint === "string" ? ex.endpoint : "";

  if (
    name === "SplitMageApiError" &&
    (status === 401 || status === 403 || status === 404) &&
    endpoint.includes("/api/splitmage/")
  ) {
    return true;
  }

  return name === "AxiosError" && status === 401;
}

/**
 * Should Drop Network Transport Event.
 */
export function shouldDropNetworkTransportEvent(
  message: string,
  originalException: unknown
): boolean {
  const exceptionMessage = getExceptionMessage(originalException);
  const combined = `${message} ${exceptionMessage}`;

  // Only drop Sentry SDK / browser transport failures — never drop app API 503/429/fetch errors.
  const sentryTransportPatterns = [
    "Failed to send event to Sentry",
    "Sentry Hub not found",
    "sentry.io",
    "ingest.sentry.io",
  ];

  return sentryTransportPatterns.some((pattern) => combined.includes(pattern));
}

export function shouldDropNetworkChangeEvent(message: string): boolean {
  return (
    message.includes("Network connection changed during request") ||
    message.includes("API Error: Network connection changed during request")
  );
}

/**
 * Should Drop Axios Timeout Noise.
 */
export function shouldDropAxiosTimeoutNoise(message: string, originalException: unknown): boolean {
  const exceptionMessage = getExceptionMessage(originalException);
  const combined = `${message} ${exceptionMessage}`;
  const isTimeout =
    combined.includes("timeout of 5000ms exceeded") ||
    combined.includes("timeout of 10000ms exceeded") ||
    combined.includes("ECONNABORTED");

  if (!isTimeout) {
    return false;
  }

  return (
    combined.includes("[useInvitations]") ||
    combined.includes("/invitations/pending") ||
    combined.includes("/invitations/")
  );
}

/**
 * Should Drop Handled Client Noise Event.
 */
export function shouldDropHandledClientNoiseEvent(
  message: string,
  originalException: unknown
): boolean {
  const exceptionMessage = getExceptionMessage(originalException);
  const combined = `${message} ${exceptionMessage}`;

  // Keep only true client UX fallbacks — never drop encryption/admin/auth failures.
  const handledPatterns = ["Automatic email failed, using mailto fallback"];

  return handledPatterns.some((pattern) => combined.includes(pattern));
}
